// ── THE RENEWAL SEASON'S PACE, AND THE ROUTE THAT GETS THROUGH IT ────────────
//
// Kevin, 2026-10-03: *"you can also add an automatic renewals per work day (M-F excluding
// holidays) needed in order to see everybody. Then also build a farm visit route optimizer.
// During renewal season we may go to 2-3 farms per day … Make it flexible so I can say how
// many farms I want to visit per day and then you select the farms by proximity and visit
// efficiency."*
//
// This renders. Every rule it draws is in `renewalSeason.js` and `visitRoute.js`, which are
// pure and tested offline — this file decides nothing.
//
// ⚠ AGENT SURFACE ONLY. It reads customer names and coordinates, which is the one thing the
// Market Intelligence map was fixed for NOT doing. The Coverage Advisor is never rendered in
// the portal (verified: the portal's "coverage" key routes to a farmer's own coverage, not
// to CoverageAdvisorView), so the read belongs here and nowhere a grower can reach.

import React from "react";
import { renewalPace, seasonWindow, nextSeason, scheduleDays } from "./renewalSeason";
import { planVisits, DEFAULT_MAX_LINK_MILES, AGENCY_ORIGIN } from "./visitRoute";
import { isCentroid } from "./farmLocation";
import { writePrintWindow } from "../printBackBar";

const C = {
  ink: "#1a2e1a", mute: "#5c6f5c", faint: "#60735f", line: "#e4ece4",
  good: "#2d6a2d", warn: "#8a6d1f", bad: "#b3300f", panel: "#fff", wash: "#f4f8f4",
};
const card = { background: C.panel, border: "1px solid " + C.line, borderRadius: 10, padding: 14 };
const lbl = { fontSize: 10, letterSpacing: ".07em", textTransform: "uppercase", color: C.mute, fontWeight: 700 };
const num = { fontFamily: "monospace", fontWeight: 800, color: C.ink };

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * The pace strip: how many are left, how many working days, and what that is a day.
 *
 * ⚠ IT PRINTS THE EXCLUDED HOLIDAYS BY NAME. Nothing in this database records which days
 * the agency is closed — the federal set is a stated convention, not a measurement — so the
 * only honest way to show the count is beside the days it took out, where Kevin can see
 * that Labor Day was removed and that Columbus Day was too.
 */
export function VisitPace({ seasonKey, cropYear, total, visited, isMobile }) {
  // The calendar year that sells this crop year, inverted from the season's own offset so
  // the strip and the roster are talking about the same season.
  const calYear = seasonKey === "spring" ? cropYear : cropYear - 1;
  const today = todayIso();
  const p = renewalPace({ total, visited, today, seasonKey, calendarYear: calYear });

  if (!p.ok) {
    const w = seasonWindow(seasonKey, calYear);
    return (
      <div style={{ ...card, background: C.wash }}>
        <div style={lbl}>Renewal pace</div>
        <div style={{ fontSize: 12.5, color: C.warn, marginTop: 6 }}>
          {p.why === "the customer count is not established"
            ? "The customer count is not established for this list yet, so no pace is stated."
            : `No pace: ${p.why}.`}
          {w && <> The {w.label} season for CY{w.cropYear} runs {w.start} to {w.end}.</>}
        </div>
      </div>
    );
  }

  const w = p.season;
  const nxt = p.notYetOpen ? null : (p.closed ? nextSeason(today) : null);
  const tone = p.unreachable ? C.bad : p.paceNow != null && p.paceAtStart != null && p.paceNow > p.paceAtStart * 1.25 ? C.warn : C.good;

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={lbl}>{w.label} renewals · CY{w.cropYear}</span>
        <span style={{ fontSize: 10.5, color: C.faint }}>{w.start} – {w.end} · {w.closes}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0,1fr))" : "repeat(4, minmax(0,1fr))", gap: 10, marginTop: 11 }}>
        <div>
          <div style={{ ...num, fontSize: 19 }}>{p.remaining.toLocaleString()}</div>
          <div style={{ fontSize: 10.5, color: C.mute }}>still to see</div>
        </div>
        <div>
          <div style={{ ...num, fontSize: 19, color: C.good }}>{p.visited.toLocaleString()}</div>
          <div style={{ fontSize: 10.5, color: C.mute }}>visited of {p.total.toLocaleString()}</div>
        </div>
        <div>
          <div style={{ ...num, fontSize: 19 }}>{p.workingDaysLeft}</div>
          <div style={{ fontSize: 10.5, color: C.mute }}>working days left</div>
        </div>
        <div>
          {/* ⚠ NO DAYS LEFT AND FARMS OUTSTANDING IS NOT A PACE OF INFINITY — it is a season
              that cannot be finished at any pace, which is a different sentence. */}
          <div style={{ ...num, fontSize: 19, color: tone }}>{p.unreachable ? "—" : p.paceNow != null ? p.paceNow.toFixed(1) : "—"}</div>
          <div style={{ fontSize: 10.5, color: C.mute }}>a day needed</div>
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: C.mute, marginTop: 9, lineHeight: 1.5 }}>
        {p.unreachable
          ? <span style={{ color: C.bad, fontWeight: 700 }}>The season has closed with {p.remaining} still to see.</span>
          : p.closed ? <>The season has closed.{nxt ? <> Next is {nxt.label}, opening {nxt.start}.</> : null}</>
          : p.notYetOpen ? <>Not open yet — {p.workingDaysLeft} working days in the window, {p.paceNow != null ? p.paceNow.toFixed(1) : "—"} a day from the start.</>
          : <>{p.workingDaysInSeason} working days in the season; it needed {p.paceAtStart != null ? p.paceAtStart.toFixed(1) : "—"} a day from the start.</>}
      </div>

      {/* The days taken out, named. A count with no list cannot be checked against the
          agency's own calendar, and this set is a convention rather than a measurement. */}
      {p.holidaysInSeason.length > 0 && (
        <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>
          Excludes weekends and {p.holidaysInSeason.map((h) => `${h.name} (${h.date})`).join(", ")}.
          Federal holidays — nothing here records the agency's own closures.
        </div>
      )}
    </div>
  );
}

/**
 * The route planner. Say how many farms a day; it groups by proximity and orders each day.
 *
 * ⚠ THE FARMS IT CANNOT PLACE ARE NAMED ON SCREEN, not dropped. A plan that silently omits
 * them hands back a season that looks complete — and 48 of 282 customers in this book have
 * neither an address nor a coordinate.
 */
// A date a human reads, from an ISO one. Built in UTC to match the rule that produced it —
// parsing "2026-09-08" as local time lands on the 7th in a negative-offset timezone and the
// card would name the wrong weekday.
const dayLabel = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
};

// ── THE PRINTED SCHEDULE ─────────────────────────────────────────────────────
// What goes in the truck. One line per stop with room to write, because the point of
// carrying it is to come back with notes on it.
//
// ⚠ EVERY CAVEAT THE SCREEN CARRIES IS ON THE PAPER TOO. A printout outlives the page it
// came from: straight-line distances, the farms that could not be placed, and the days the
// season has no room for all travel with it, or somebody reads a tidy schedule in three
// weeks and takes it for a complete plan.
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// ⚠ THE HTML IS BUILT BY ITS OWN EXPORTED FUNCTION, SEPARATE FROM THE WINDOW HANDLING.
// Not tidiness: while the caveats were asserted by grepping this file, a mutation that made
// the unplaceable block UNREACHABLE left its string literal in the source and the check
// stayed green — the comment-satisfaction trap in a new costume. A rule that cannot be
// exercised through the front door is one that gets silently reversed, so the builder is
// pure, returns a string, and the checker drives it.
export function buildScheduleHtml(plan, sched, perDay, maxLink) {
  if (!plan) return null;
  const dayRows = plan.days.map((d) => {
    const when = sched && sched.dates[d.day - 1] ? dayLabel(sched.dates[d.day - 1]) : null;
    const stops = d.stops.map((s, i) => `
      <tr>
        <td class="n">${i + 1}</td>
        <td class="f">${esc(s.name)}${s.county ? `<span class="c"> · ${esc(s.county)}</span>` : ""}${isCentroid(s.locSource) ? `<span class="c"> · field centre, not the farmstead</span>` : ""}</td>
        <td class="note"></td>
      </tr>`).join("");
    return `
      <section>
        <h2>${when ? esc(when) : `Day ${d.day}`}
          <span class="mi">${d.miles} mi${d.longestLegMiles > 0 ? ` · longest leg ${d.longestLegMiles} mi` : ""}</span>
        </h2>
        ${when ? "" : `<p class="warn">No day left in the season for this one.</p>`}
        ${d.short ? `<p class="warn">${d.stops.length} of ${perDay} — nobody else left unplanned within ${maxLink} mi.</p>` : ""}
        <table><tbody>${stops}</tbody></table>
      </section>`;
  }).join("");

  // Counted from the stops the sheet is ACTUALLY printing, not from the roster — a farm
  // filtered out of the plan must not be counted in its caveat.
  const centroidCount = plan.days.reduce((a, d) => a + d.stops.filter((x) => isCentroid(x.locSource)).length, 0);
  const unplaced = plan.unplaceable.length
    ? `<section class="brk"><h2>Not on any route — ${plan.unplaceable.length}</h2>
       <p class="warn">These customers still need seeing. They could not be placed because we have no coordinates for them.</p>
       <ul>${plan.unplaceable.map((u) => `<li>${esc(u.name)} — ${esc(u.why)}</li>`).join("")}</ul></section>`
    : "";

  return `<!doctype html><html><head><meta charset="utf-8"><title>Renewal visit schedule</title><style>
    @page { margin: 14mm; }
    body { font: 12px/1.45 system-ui, -apple-system, "Segoe UI", sans-serif; color: #1a2e1a; margin: 0; }
    h1 { font-size: 17px; margin: 0 0 2px; }
    .sub { font-size: 11px; color: #5c6f5c; margin: 0 0 14px; }
    section { break-inside: avoid; border: 1px solid #d8e4d8; border-radius: 6px; padding: 9px 11px; margin-bottom: 9px; }
    section.brk { break-before: page; }
    h2 { font-size: 13px; margin: 0 0 6px; display: flex; justify-content: space-between; align-items: baseline; }
    .mi { font-size: 10.5px; color: #5c6f5c; font-weight: 400; font-family: ui-monospace, monospace; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 5px 4px; vertical-align: bottom; }
    td.n { width: 16px; color: #60735f; font-size: 10.5px; }
    td.f { font-weight: 600; white-space: nowrap; }
    td.c, .c { font-weight: 400; color: #5c6f5c; }
    td.note { border-bottom: 1px dotted #c8d6c8; }
    .warn { color: #8a6d1f; font-size: 10.5px; margin: 0 0 6px; }
    ul { margin: 6px 0 0; padding-left: 18px; font-size: 11px; color: #5c6f5c; }
    footer { margin-top: 12px; font-size: 10px; color: #60735f; line-height: 1.5; }
  </style></head><body>
    <h1>Renewal visit schedule${sched && sched.season ? ` — ${esc(sched.season.label)} CY${sched.season.cropYear}` : ""}</h1>
    <p class="sub">${plan.placed} farm${plan.placed === 1 ? "" : "s"} over ${plan.days.length} day${plan.days.length === 1 ? "" : "s"}
      at ${perDay} a day · ${plan.totalMiles.toLocaleString()} mi total
      ${sched && sched.season ? ` · season ${esc(sched.season.start)} to ${esc(sched.season.end)}` : ""}</p>
    ${dayRows}
    ${unplaced}
    <footer>
      Distances are ${esc(plan.basis)}.
      ${centroidCount > 0 ? ` ${centroidCount} of these stops ${centroidCount === 1 ? "was" : "were"} placed at the centre of the farm's own fields rather than at an address we could map, so the drive to the yard may differ.` : ""}
      ${sched && sched.shortBy > 0 ? ` ${sched.shortBy} day${sched.shortBy === 1 ? "" : "s"} of this plan have no working day left in the season.` : ""}
      Printed ${esc(new Date().toLocaleString())}.
    </footer>
  </body></html>`;
}

// The thin half: open a window and hand it the document. A blocked pop-up is the browser's
// call, not an error worth throwing.
function printSchedule(plan, sched, perDay, maxLink) {
  const html = buildScheduleHtml(plan, sched, perDay, maxLink);
  if (!html) return;
  const w = window.open("", "_blank");
  if (!w) return;
  writePrintWindow(w, html);
  w.focus();
  w.print();
}

export function VisitRoutePlanner({ farms, isMobile, workingDaysLeft = null, seasonKey = null, cropYear = null,
                                    outstandingTotal = null }) {
  // ⚠ THE BOX HOLDS TEXT; THE PLAN HOLDS A NUMBER. Kevin, 2026-10-04: *"The type boxes for
  // miles apart and farms per day doesn't work right, it's very glitchy."* They clamped on
  // EVERY KEYSTROKE — `setMaxLink(Math.max(5, Math.min(400, Number(e.target.value) || 60)))`
  // — which breaks typing three separate ways:
  //
  //   · clearing the box is impossible. `Number("")` is 0, so the value snaps back the
  //     instant the field is empty and a fresh number can never be started;
  //   · any number whose FIRST DIGIT is below the minimum cannot be typed at all. To enter
  //     100 you press `1`, which clamps to 5, so the next keystroke gives 50;
  //   · the plan is a pure function of these two, so every intermediate keystroke re-planned
  //     124 farms synchronously — which on a phone is the lag that reads as "glitchy".
  //
  // So the text is free while editing and the NUMBER is committed on blur or Enter. That is
  // the shape the per-acre input program already uses, for the same reason.
  const [perDay, setPerDay] = React.useState(3);
  const [perDayText, setPerDayText] = React.useState("3");
  // ⚠ EDITABLE, BECAUSE NOBODY SOURCED IT. Nothing records how far Kevin will drive between
  // two farms; 60 straight-line miles is a stated default and a visible control, which is
  // the honest shape for a number this app cannot establish.
  const [maxLink, setMaxLink] = React.useState(DEFAULT_MAX_LINK_MILES);
  const [maxLinkText, setMaxLinkText] = React.useState(String(DEFAULT_MAX_LINK_MILES));

  // Commit a typed box: take the digits, clamp, and write the clamped value BACK into the
  // box so what is on screen is always what the plan used. An unreadable or empty box
  // settles to the fallback rather than to zero — a reach of 0 places nobody, and a blank
  // control that silently plans nothing is worse than one that shows its default.
  const commitNum = (text, lo, hi, fallback, setNum, setText) => {
    const digits = String(text == null ? "" : text).replace(/[^0-9]/g, "");
    const n = parseInt(digits, 10);
    const v = Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : fallback;
    setNum(v);
    setText(String(v));
  };
  const [showUnplaceable, setShowUnplaceable] = React.useState(false);

  // ⚠ useMemo, NOT useEffect-INTO-STATE, AND THE RENDER HARNESS IS WHAT FOUND IT. The plan
  // is a pure function of (farms, perDay, maxLink) — there is nothing asynchronous about it
  // — but it was computed in an effect and held in state, which meant the component returned
  // NULL on its first pass and only filled in on the second. In the browser that is a blank
  // frame; under renderToStaticMarkup, where effects never run, it is a planner that renders
  // nothing at all and every assertion about its content failing at once. Re-planning is
  // cheap and runs on change rather than behind a button: a stale plan beside a changed
  // number is a plan for a day nobody asked for.
  const plan = React.useMemo(
    () => (farms && farms.length ? planVisits({ farms, perDay, maxLinkMiles: maxLink, origin: AGENCY_ORIGIN }) : null),
    [farms, perDay, maxLink]
  );

  // ── THE DATES THE PLAN WOULD BE DRIVEN ON ──────────────────────────────────
  // Kevin asked for a SCHEDULE, and "Day 1, Day 2, Day 3" is a grouping. This maps the
  // groups onto the season's actual working days.
  //
  // ⚠ IT RETURNS FEWER DATES THAN THERE ARE DAYS RATHER THAN RUNNING PAST THE SEASON, so a
  // 44-day plan in a 21-day window shows 21 dated days and 23 undated ones — which is the
  // finding, and a calendar that ran into October would hide it.
  const sched = React.useMemo(() => {
    if (!plan || !seasonKey || !Number.isFinite(cropYear)) return null;
    const calYear = seasonKey === "spring" ? cropYear : cropYear - 1;
    return scheduleDays({ seasonKey, calendarYear: calYear, count: plan.days.length, from: todayIso() });
  }, [plan, seasonKey, cropYear]);

  if (!farms || !farms.length) {
    return (
      <div style={{ ...card, background: C.wash }}>
        <div style={lbl}>Visit route</div>
        <div style={{ fontSize: 12.5, color: C.mute, marginTop: 6 }}>
          Nobody is outstanding on this list, so there is no route to plan.
        </div>
      </div>
    );
  }
  if (!plan) return null;

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={lbl}>Visit route · {plan.days.length} day{plan.days.length === 1 ? "" : "s"}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: C.mute }}>
            farms per day
            <input type="number" inputMode="numeric" min={1} max={12} value={perDayText}
              onChange={(e) => setPerDayText(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => commitNum(perDayText, 1, 12, 3, setPerDay, setPerDayText)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              style={{ width: 54, padding: "6px 8px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: "monospace", fontSize: 13, minHeight: 36 }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: C.mute }}
            title="How far apart two farms may be and still share a day. Straight-line miles.">
            max mi apart
            <input type="number" inputMode="numeric" min={5} max={400} step={5} value={maxLinkText}
              onChange={(e) => setMaxLinkText(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => commitNum(maxLinkText, 5, 400, DEFAULT_MAX_LINK_MILES, setMaxLink, setMaxLinkText)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              style={{ width: 62, padding: "6px 8px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: "monospace", fontSize: 13, minHeight: 36 }} />
          </label>
          {/* The thing you actually carry. window.print() in a new window is the pattern the
              LRP monthly summary and the target sheet already use — no new dependency. */}
          <button type="button" onClick={() => printSchedule(plan, sched, perDay, maxLink)}
            style={{ border: "1px solid #cfdccf", background: "#f7faf7", color: "#2d4a2d", fontFamily: "inherit", fontSize: 12, fontWeight: 700, padding: "7px 11px", borderRadius: 6, cursor: "pointer", minHeight: 36 }}>
            Print schedule
          </button>
        </div>
      </div>

      {/* ⚠ DOES THE PLAN FIT THE SEASON? Measured on the live book: 124 outstanding wheat
          customers at 3 a day is 42 days, and the fall season holds 21 working days. Both
          halves were already on this screen and neither said the other was impossible, which
          is the sort of silence that lets a season be planned and then missed. */}
      {workingDaysLeft != null && plan.days.length > workingDaysLeft && (
        <div style={{ marginTop: 9, padding: "9px 11px", border: "1px solid #e8d8a8", background: "#fff8e8", borderRadius: 8, fontSize: 12, color: C.warn, lineHeight: 1.5 }}>
          {/* ⚠ ZERO DAYS LEFT IS NOT A RATE. `Math.ceil(placed / Math.max(1, 0))` renders as
              "124 a day", which is a target somebody could read as achievable; it is the
              divide-by-zero this feature's own pace rule already refuses, papered over by a
              max(). A closed season is a different sentence and gets one. */}
          {workingDaysLeft === 0 ? (
            <>This plan needs <strong>{plan.days.length} days</strong> and the season has <strong>no working days left</strong>.
            {" "}{plan.placed} farm{plan.placed === 1 ? "" : "s"} cannot be seen inside it at any pace.</>
          ) : (
            <>This plan needs <strong>{plan.days.length} days</strong> and the season has{" "}
            <strong>{workingDaysLeft}</strong> working day{workingDaysLeft === 1 ? "" : "s"} left.
            {" "}At {perDay} a day it does not fit — {plan.placed} farms over {workingDaysLeft} day
            {workingDaysLeft === 1 ? "" : "s"} is <strong>{Math.ceil(plan.placed / workingDaysLeft)} a day</strong>.</>
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>
        {/* ⚠ THE ROUTE'S DENOMINATOR IS NOT THE PACE CARD'S. The pace strip counts EVERY
            outstanding customer; the route is handed the FILTERED rows, so with a filter on
            the two disagree — a live screenshot showed "124 still to see" beside "47 farms
            planned" with nothing to reconcile them, which reads as the planner silently
            dropping 77 farms. It states its own denominator, and only where they differ, so
            the usual case stays short. */}
        {plan.placed} farm{plan.placed === 1 ? "" : "s"}
        {outstandingTotal != null && farms && outstandingTotal > farms.length
          ? <> of the {outstandingTotal} still to see — <strong>the other {outstandingTotal - farms.length} are hidden
            by the filter above</strong>, not dropped</> : null}
        {" "}planned over {plan.days.length} day
        {plan.days.length === 1 ? "" : "s"} · {plan.totalMiles.toLocaleString()} mi total.
        {/* ⚠ STRAIGHT LINE, AND IT SAYS SO. There is no routing service here; a minutes
            figure off great-circle miles would be invented. */}
        {" "}Distances are {plan.basis}.
        {/* ⚠ NEVER SILENT. A schedule that placed a quarter of the book by field centroid
            and said nothing reads as a plan built from addresses. Counted off the stops the
            plan actually holds, not off the roster. */}
        {(() => {
          const n = plan.days.reduce((a, d) => a + d.stops.filter((x) => isCentroid(x.locSource)).length, 0);
          if (!n) return null;
          return (
            <span style={{ color: C.warn }}>
              {" "}{n} {n === 1 ? "stop is" : "stops are"} placed at the centre of the farm's own
              fields — we have no map-able address for {n === 1 ? "it" : "them"}, so the drive to
              the yard may differ.
            </span>
          );
        })()}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))", gap: 10, marginTop: 11 }}>
        {plan.days.map((d) => (
          <div key={d.day} style={{ border: "1px solid " + C.line, borderRadius: 8, padding: 10, background: C.wash }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              {/* The DATE where the season can carry one, the ordinal where it cannot. An
                  undated day is one the season has no room for, which is the finding — so it
                  keeps its number and says nothing it cannot support. */}
              <span style={{ fontSize: 12.5, fontWeight: 800, color: C.ink }}>
                {sched && sched.dates[d.day - 1] ? dayLabel(sched.dates[d.day - 1]) : `Day ${d.day}`}
              </span>
              <span style={{ fontFamily: "monospace", fontSize: 11.5, color: C.mute }}>{d.miles} mi</span>
            </div>
            {sched && !sched.dates[d.day - 1] && (
              <div style={{ fontSize: 10, color: C.warn, marginTop: 2 }}>no day left in the season for this</div>
            )}
            <ol style={{ margin: "7px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: C.ink }}>
              {d.stops.map((s) => (
                <li key={s.id}>
                  {s.name}
                  {s.county ? <span style={{ color: C.faint }}> · {s.county}</span> : null}
                  {/* ⚠ A STOP PLACED BY FIELD CENTROID IS MARKED. It is the middle of their
                      ground, not the farmstead — measured median 1.5 mi from the address
                      where both exist, but p90 25 mi. Unmarked it reads as an address. */}
                  {isCentroid(s.locSource) ? (
                    <span style={{ color: C.warn, fontSize: 10.5 }} title={`Placed by the ${s.locLabel} (${s.locFieldCount} fields) — we have no map-able address for this farm`}>
                      {" "}· field centre
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
            {/* A day of three farms averaging 12 miles with one 40-mile leg is a different
                day from three evenly spread, so the longest hop is named. */}
            {d.longestLegMiles > 0 && (
              <div style={{ fontSize: 10.5, color: C.faint, marginTop: 6 }}>
                longest leg {d.longestLegMiles} mi · ordered {d.method}
              </div>
            )}
            {/* ⚠ WHY THIS DAY IS WHERE IT IS. The days are ordered by their largest customer's
                prior-year premium, so an agent reading "Day 3" is entitled to see the number
                that put it there — an ordering nobody can check is one nobody will trust, and
                this one is deliberately NOT what decides who shares a day. */}
            {(() => {
              const top = Math.max(...d.stops.map((s) => Number(s.size) || 0));
              if (!(top > 0)) return null;
              return (
                <div style={{ fontSize: 10.5, color: C.faint, marginTop: 3 }}>
                  biggest here paid {top >= 1000 ? "$" + Math.round(top / 1000) + "k" : "$" + Math.round(top)} last year
                </div>
              );
            })()}
            {/* A day with fewer stops than asked for is not a book that ran out of customers
                — the next one was simply too far to join. Saying so is what stops a reader
                re-running the plan looking for the farms they think went missing. */}
            {d.short && (
              <div style={{ fontSize: 10.5, color: C.warn, marginTop: 4 }}>
                {/* ⚠ "nothing else" WAS FALSE. Measured on the live roster 2026-10-04, 7 of 17
                    short days had a farm INSIDE the stated reach — Paul Long 2.9 mi, Shaffer 3.5,
                    Du Mond 5.9, Rauberdale 6.8. The neighbour was simply already taken by an
                    earlier day. The day is short because nobody UNPLANNED was near enough, which
                    is a fact about what is left, not about the map. */}
                {d.stops.length} of {plan.perDay} — nobody else left unplanned within {plan.maxLinkMiles} mi
              </div>
            )}
          </div>
        ))}
      </div>

      {plan.unplaceable.length > 0 && (
        <div style={{ marginTop: 11, borderTop: "1px solid " + C.line, paddingTop: 10 }}>
          <button type="button" onClick={() => setShowUnplaceable((v) => !v)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: C.warn, minHeight: 36 }}>
            ⚠ {plan.unplaceable.length} customer{plan.unplaceable.length === 1 ? "" : "s"} could not be placed on a route — {showUnplaceable ? "hide" : "show"}
          </button>
          {showUnplaceable && (
            <ul style={{ margin: "7px 0 0", paddingLeft: 18, fontSize: 11.5, lineHeight: 1.6, color: C.mute }}>
              {plan.unplaceable.map((u) => <li key={u.id}>{u.name} — {u.why}</li>)}
            </ul>
          )}
          <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>
            They are still on the renewal list and still need seeing; they just cannot be routed.
          </div>
        </div>
      )}
    </div>
  );
}
